import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  NgbDate,
  NgbDateStruct,
  NgbModal,
  NgbModalRef,
} from "@ng-bootstrap/ng-bootstrap";
import { LocationAvailabilityEnum } from "src/app/shared/enum/location-availability";
import { AdvertisingCampaignInterface } from "src/app/shared/models/advertising-campaigns/advertising-campaign.interface";
import { DateFilter } from "src/app/shared/models/date-filte.interface";
import { DeleteComponent } from "../../drop-zone-options/modal/delete/delete.component";
import { UtilFunctions } from "src/app/utils/utilFunctions";

@Component({
  selector: "app-update-booking-dates",
  templateUrl: "./update-booking-dates.component.html",
})
export class UpdateBookingDatesComponent implements OnInit {
  @Input() availableDates: DateFilter[];
  @Input() companyName: string;
  @Input() availabilityStatus: LocationAvailabilityEnum;
  @Input() unavailableDates: DateFilter[];
  @Input() advertisingCampaign: AdvertisingCampaignInterface;
  @Input() endDate: Date;
  @Input() startDate: Date;
  @Output() newAvailableDatesForLocationPartner = new EventEmitter<
    DateFilter[]
  >();
  @Output() closeTheModal: EventEmitter<boolean> = new EventEmitter();
  @Output() confirmDeletion: EventEmitter<boolean> = new EventEmitter();
  @Input() selectLocationsWithAvailabilitiesModal: NgbModalRef;
  locationAvailabilityEnum = LocationAvailabilityEnum;
  loading: boolean;

  model: NgbDateStruct;
  date: { year: number; month: number };
  flattenAvailableDates: Date[] = [];
  flattenUnavailableDates: Date[] = [];

  availableDatesNotBooked: Date[] = [];
  constructor(private modalService: NgbModal) {}

  ngOnInit(): void {
    this.flattenAvailableDates = this.flattenDateFiltersToDates(
      this.availableDates
    );
    this.flattenUnavailableDates = this.flattenDateFiltersToDates(
      this.unavailableDates
    );
    this.getAvailableDatesNotBooked();
  }

  getAvailableDatesNotBooked(): Date[] {
    if (!(this.startDate instanceof Date)) {
      this.startDate = new Date(this.startDate);
    }
    if (!(this.endDate instanceof Date)) {
      this.endDate = new Date(this.endDate);
    }

    const oneDayInMs = 24 * 60 * 60 * 1000;

    for (
      let d = new Date(this.startDate);
      d <= this.endDate;
      d = new Date(d.getTime() + oneDayInMs)
    ) {
      if (
        !this.isInDates(d, this.flattenAvailableDates) &&
        !this.isInDates(d, this.flattenUnavailableDates)
      ) {
        this.availableDatesNotBooked.push(new Date(d));
      }
    }
    return this.availableDatesNotBooked;
  }

  close() {
    this.closeTheModal.emit(true);
  }

  update() {
    this.availableDates = this.groupDatesIntoRanges(this.flattenAvailableDates);
    this.newAvailableDatesForLocationPartner.emit(this.availableDates);
  }

  private toJSDate(ngbDate: NgbDate): Date {
    return new Date(ngbDate.year, ngbDate.month - 1, ngbDate.day);
  }

  isInDates(date: Date, dates: Date[]): boolean {
    const target = date.toISOString().split("T")[0];
    return dates.some((d) => d.toISOString().split("T")[0] === target);
  }

  isUnavailable(date: NgbDate): boolean {
    const jsDate = this.toJSDate(date);
    return this.isInDates(jsDate, this.flattenUnavailableDates);
  }

  isAvailable(date: NgbDate): boolean {
    const jsDate = this.toJSDate(date);
    return this.isInDates(jsDate, this.flattenAvailableDates);
  }

  onDateSelect(date: NgbDate): void {
    const jsDate = this.toJSDate(date);

    const isInAvailable = this.flattenAvailableDates.some((d) =>
      UtilFunctions.isSameDay(d, jsDate)
    );
    const isInNotBooked = this.availableDatesNotBooked.some((d) =>
      UtilFunctions.isSameDay(d, jsDate)
    );

    if (isInAvailable) {
      this.flattenAvailableDates = this.flattenAvailableDates.filter(
        (d) => !UtilFunctions.isSameDay(d, jsDate)
      );
      if (!isInNotBooked) {
        this.availableDatesNotBooked = [
          ...this.availableDatesNotBooked,
          jsDate,
        ];
      }
    } else {
      this.flattenAvailableDates = [...this.flattenAvailableDates, jsDate];
      this.availableDatesNotBooked = this.availableDatesNotBooked.filter(
        (d) => !UtilFunctions.isSameDay(d, jsDate)
      );
    }
  }
  flattenDateFiltersToDates(dateFilters: DateFilter[]): Date[] {
    const dates: Date[] = [];
    dateFilters.forEach((filter) => {
      if (filter.startDate && filter.endDate) {
        let current = new Date(filter.startDate);
        const end = new Date(filter.endDate);

        while (current <= end) {
          dates.push(new Date(current));
          current.setDate(current.getDate() + 1);
        }
      }
    });

    return dates;
  }

  isDateDisabled = (
    date: { year: number; month: number; day: number } | null | undefined
  ): boolean => {
    if (!date) return false;
    const currentDate = new Date(date.year, date.month - 1, date.day);

    const isBeforeStart =
      this.startDate && currentDate < new Date(this.startDate);
    const isAfterEnd = this.endDate && currentDate > new Date(this.endDate);

    if (isBeforeStart || isAfterEnd) {
      return true;
    }
    return this.flattenUnavailableDates.some((unavailableDate) => {
      return (
        unavailableDate.getFullYear() === date.year &&
        unavailableDate.getMonth() + 1 === date.month &&
        unavailableDate.getDate() === date.day
      );
    });
  };

  groupDatesIntoRanges(dates: Date[]): DateFilter[] {
    if (dates.length === 0) return [];

    const sortedDates = dates.sort((a, b) => a.getTime() - b.getTime());

    const ranges: DateFilter[] = [];
    let start = sortedDates[0];
    let end = sortedDates[0];

    for (let i = 1; i < sortedDates.length; i++) {
      const current = sortedDates[i];
      const previous = new Date(end);
      previous.setDate(previous.getDate() + 1);

      if (current.getTime() === previous.getTime()) {
        end = current;
      } else {
        ranges.push({ startDate: start, endDate: end });
        start = end = current;
      }
    }

    ranges.push({ startDate: start, endDate: end });

    return ranges;
  }

  showDeleteModal() {
    const modal = this.modalService.open(DeleteComponent, {
      centered: true,
      size: "sm",
    });
    modal.componentInstance.name = "locationPartnerLowercase";
    modal.componentInstance.reference = this.companyName;
    modal.componentInstance.title = "warning";
    modal.componentInstance.emitClose = true;

    modal.componentInstance.confirmAction.subscribe((value: boolean) => {
      if (value) {
        this.confirmDeletion.emit(true);
        modal.close();
        this.selectLocationsWithAvailabilitiesModal.close();
      }
    });

    modal.componentInstance.closeTheModal.subscribe((value: boolean) => {
      if (value) {
        modal.close();
      }
    });
  }
}
